import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Camera, AlertCircle, MessageSquare, Sparkles, Upload, Trash2, Pencil } from 'lucide-react';
import { apiClient } from '../../services/api/apiClient';
import { toBlob } from 'html-to-image';
import clsx from 'clsx';
import { TWInput } from '../ui/TWInput';
import { TWSelect } from '../ui/TWSelect';
import { TWCheckbox } from '../ui/TWCheckbox';
import { ScreenshotEditor } from './ScreenshotEditor';

interface FeedbackData {
  feedbackType: 'feedback' | 'problem' | 'feature';
  subject: string;
  description: string;
  reproducibility?: string;
  stepsToReproduce?: string;
  expectedBehavior?: string;
  actualBehavior?: string;
  useCase?: string;
  priority?: 'low' | 'medium' | 'high' | 'critical';
  browserInfo?: string;
  systemInfo?: any;
  urlContext?: string;
  isPublic: boolean;
  createGithubIssue: boolean;
  screenshots: File[];
}

interface FeedbackSubmissionPanelTWProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmitSuccess?: () => void;
  prefilledData?: {
    feedbackType?: 'feedback' | 'problem' | 'feature';
    subject?: string;
    description?: string;
  };
}

export const FeedbackSubmissionPanelTW: React.FC<FeedbackSubmissionPanelTWProps> = ({
  isOpen,
  onClose,
  onSubmitSuccess,
  prefilledData
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollableRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<'feedback' | 'problem' | 'feature'>('feedback');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [characterCount, setCharacterCount] = useState(0);
  const [screenshots, setScreenshots] = useState<File[]>([]);
  const [screenshotPreviews, setScreenshotPreviews] = useState<string[]>([]);
  const [isCapturingScreenshot, setIsCapturingScreenshot] = useState(false);

  const [formData, setFormData] = useState<FeedbackData>({
    feedbackType: 'feedback',
    subject: '',
    description: '',
    reproducibility: 'always',
    stepsToReproduce: '',
    expectedBehavior: '',
    actualBehavior: '',
    useCase: '',
    priority: 'medium',
    browserInfo: navigator.userAgent,
    systemInfo: {
      platform: navigator.platform || 'Unknown',
      userAgent: navigator.userAgent,
      language: navigator.language,
      languages: navigator.languages?.join(', ') || navigator.language,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      availableScreenSize: `${window.screen.availWidth}x${window.screen.availHeight}`,
      colorDepth: window.screen.colorDepth,
      pixelRatio: window.devicePixelRatio || 1,
      windowSize: `${window.innerWidth}x${window.innerHeight}`,
      documentSize: `${document.documentElement.clientWidth}x${document.documentElement.clientHeight}`,
      cookiesEnabled: navigator.cookieEnabled,
      onlineStatus: navigator.onLine,
      doNotTrack: navigator.doNotTrack,
      deviceType: /Mobile|Android|iPhone|iPad/i.test(navigator.userAgent) ? 'Mobile' : 'Desktop',
      touchSupport: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timezoneOffset: new Date().getTimezoneOffset(),
      locale: navigator.language,
      ...(navigator as any).deviceMemory && { deviceMemory: (navigator as any).deviceMemory + ' GB' },
      ...(navigator as any).hardwareConcurrency && { cpuCores: (navigator as any).hardwareConcurrency }
    },
    urlContext: window.location.href,
    isPublic: false,
    createGithubIssue: true,
    screenshots: []
  });
  const [selectedPreview, setSelectedPreview] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Disable Lenis scroll when panel is open
  useEffect(() => {
    if (isOpen) {
      const lenisInstance = (window as any).lenis;
      if (lenisInstance) {
        lenisInstance.stop();
      }
      document.body.style.overflow = 'hidden';

      return () => {
        if (lenisInstance) {
          lenisInstance.start();
        }
        document.body.style.overflow = '';
      };
    }
    return undefined;
  }, [isOpen]);

  // Auto-save to localStorage every 30 seconds
  useEffect(() => {
    const saveInterval = setInterval(() => {
      if (formData.subject?.trim() || formData.description?.trim() || screenshotPreviews.length > 0) {
        localStorage.setItem('feedbackDraft', JSON.stringify(formData));
        if (screenshotPreviews.length > 0) {
          localStorage.setItem('feedbackScreenshots', JSON.stringify(screenshotPreviews));
        }
      } else {
        localStorage.removeItem('feedbackDraft');
        localStorage.removeItem('feedbackScreenshots');
      }
    }, 30000);

    return () => clearInterval(saveInterval);
  }, [formData, screenshotPreviews]);

  // Load draft from localStorage on mount
  useEffect(() => {
    const justSubmitted = sessionStorage.getItem('feedbackJustSubmitted');
    if (justSubmitted) {
      sessionStorage.removeItem('feedbackJustSubmitted');
      localStorage.removeItem('feedbackDraft');
      localStorage.removeItem('feedbackScreenshots');
      return;
    }

    if (prefilledData) {
      setFormData(prev => ({
        ...prev,
        feedbackType: prefilledData.feedbackType || 'feature',
        subject: prefilledData.subject || '',
        description: prefilledData.description || ''
      }));
      setActiveTab(prefilledData.feedbackType || 'feature');
      return;
    }

    const draft = localStorage.getItem('feedbackDraft');
    if (draft) {
      try {
        const parsedDraft = JSON.parse(draft);
        setFormData(parsedDraft);
        setActiveTab(parsedDraft.feedbackType);
      } catch (e) {
        console.error('Failed to load draft:', e);
      }
    }

    const savedScreenshots = localStorage.getItem('feedbackScreenshots');
    if (savedScreenshots) {
      try {
        const parsedScreenshots = JSON.parse(savedScreenshots);
        setScreenshotPreviews(parsedScreenshots);

        const filePromises = parsedScreenshots.map(async (dataUrl: string, index: number) => {
          const response = await fetch(dataUrl);
          const blob = await response.blob();
          return new File([blob], `restored-screenshot-${index}.webp`, { type: 'image/webp' });
        });

        Promise.all(filePromises).then(files => {
          setScreenshots(files);
        });
      } catch (e) {
        console.error('Failed to load screenshots:', e);
      }
    }
  }, [isOpen, prefilledData]);

  const handleTabChange = (tab: 'feedback' | 'problem' | 'feature') => {
    setActiveTab(tab);
    setFormData(prev => ({
      ...prev,
      feedbackType: tab,
      isPublic: tab === 'problem' || tab === 'feature' ? true : false,
      createGithubIssue: true
    }));
  };

  const handleInputChange = (field: keyof FeedbackData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'description') {
      setCharacterCount(value.length);
    }
  };

  // Selektoren für sensible Daten, die automatisch geblurrt werden
  const SENSITIVE_SELECTORS = [
    'input[type="email"]',
    'input[type="password"]',
    'input[name*="email"]',
    'input[name*="name"]',
    'input[name*="iban"]',
    'input[name*="phone"]',
    'input[name*="telefon"]',
    '[data-sensitive]',  // Explizit markierte Elemente
  ];

  // Elemente vor Screenshot blurren und danach wiederherstellen
  const blurSensitiveElements = (): HTMLElement[] => {
    const blurredElements: HTMLElement[] = [];

    SENSITIVE_SELECTORS.forEach(selector => {
      document.querySelectorAll(selector).forEach(el => {
        if (el instanceof HTMLElement && !el.hasAttribute('data-no-blur')) {
          // Ursprünglichen Filter-Wert speichern
          el.dataset.originalFilter = el.style.filter || '';
          el.style.filter = 'blur(8px)';
          blurredElements.push(el);
        }
      });
    });

    return blurredElements;
  };

  const unblurElements = (elements: HTMLElement[]) => {
    elements.forEach(el => {
      el.style.filter = el.dataset.originalFilter || '';
      delete el.dataset.originalFilter;
    });
  };

  const captureScreenshot = async () => {
    let blurredElements: HTMLElement[] = [];

    try {
      setIsCapturingScreenshot(true);

      // Sensible Elemente blurren
      blurredElements = blurSensitiveElements();

      // Warten bis Browser das Blur gerendert hat (2 Frames)
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      // Extra Verzögerung für Sicherheit
      await new Promise(resolve => setTimeout(resolve, 100));

      // html-to-image: Direkt vom Body screenshotten
      const blob = await toBlob(document.body, {
        quality: 0.95,
        pixelRatio: 1,
        width: window.innerWidth,
        height: window.innerHeight,
        style: {
          // Aktuelle Scroll-Position berücksichtigen
          transform: `translateY(-${window.scrollY}px)`,
        },
        filter: (node) => {
          // Feedback-Panel und noscript aus Screenshot ausschließen
          if (node.nodeName === 'NOSCRIPT') {
            return false;
          }
          if (node instanceof HTMLElement) {
            return node.id !== 'feedback-panel' && !node.classList.contains('z-[10002]');
          }
          return true;
        }
      });

      // Blur wieder entfernen
      unblurElements(blurredElements);

      setIsCapturingScreenshot(false);

      if (blob && screenshots.length < 4) {
        const file = new File([blob], `screenshot-${Date.now()}.png`, { type: 'image/png' });
        setScreenshots(prev => [...prev, file]);

        const reader = new FileReader();
        reader.onload = (e) => {
          const preview = e.target?.result as string;
          setScreenshotPreviews(prev => {
            const newPreviews = [...prev, preview];
            localStorage.setItem('feedbackScreenshots', JSON.stringify(newPreviews));
            return newPreviews;
          });
        };
        reader.readAsDataURL(file);
      } else if (!blob) {
        alert('Screenshot konnte nicht erstellt werden. Bitte versuchen Sie es erneut.');
      }
    } catch (error: any) {
      // Im Fehlerfall auch Blur entfernen
      unblurElements(blurredElements);
      console.error('Failed to capture screenshot:', error);
      setIsCapturingScreenshot(false);
      alert('Screenshot konnte nicht erstellt werden.\n\nBitte nutzen Sie die Upload-Funktion:\n- Windows: Win + Shift + S\n- Mac: Cmd + Shift + 4');
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(file =>
      file.type.startsWith('image/') && file.size <= 10485760
    ).slice(0, 4 - screenshots.length);

    validFiles.forEach(file => {
      setScreenshots(prev => [...prev, file]);

      const reader = new FileReader();
      reader.onload = (e) => {
        setScreenshotPreviews(prev => [...prev, e.target?.result as string]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeScreenshot = (index: number) => {
    setScreenshots(prev => prev.filter((_, i) => i !== index));
    setScreenshotPreviews(prev => {
      const newPreviews = prev.filter((_, i) => i !== index);
      if (newPreviews.length > 0) {
        localStorage.setItem('feedbackScreenshots', JSON.stringify(newPreviews));
      } else {
        localStorage.removeItem('feedbackScreenshots');
      }
      return newPreviews;
    });
  };

  // Bearbeiteten Screenshot speichern
  const handleSaveEditedScreenshot = async (editedDataUrl: string) => {
    if (!selectedPreview) return;

    const index = screenshotPreviews.indexOf(selectedPreview);
    if (index === -1) return;

    // DataURL zu File konvertieren
    const response = await fetch(editedDataUrl);
    const blob = await response.blob();
    const file = new File([blob], `screenshot-edited-${Date.now()}.png`, { type: 'image/png' });

    // Screenshots und Previews aktualisieren
    setScreenshots(prev => {
      const newScreenshots = [...prev];
      newScreenshots[index] = file;
      return newScreenshots;
    });

    setScreenshotPreviews(prev => {
      const newPreviews = [...prev];
      newPreviews[index] = editedDataUrl;
      localStorage.setItem('feedbackScreenshots', JSON.stringify(newPreviews));
      return newPreviews;
    });

    // Editor schließen und Preview aktualisieren
    setIsEditing(false);
    setSelectedPreview(editedDataUrl);
  };

  const handleSubmit = async () => {
    if (!formData.subject || formData.subject.trim().length < 5) {
      alert('Der Betreff muss mindestens 5 Zeichen lang sein');
      return;
    }

    if (!formData.description || formData.description.trim().length < 10) {
      alert('Die Beschreibung muss mindestens 10 Zeichen lang sein');
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = {
        feedbackType: formData.feedbackType,
        subject: formData.subject.trim(),
        description: formData.description.trim(),
        priority: formData.priority,
        isPublic: formData.isPublic,
        createGithubIssue: formData.createGithubIssue,
        browserInfo: formData.browserInfo,
        systemInfo: formData.systemInfo,
        urlContext: formData.urlContext,
        ...(formData.feedbackType === 'problem' && {
          reproducibility: formData.reproducibility,
          stepsToReproduce: formData.stepsToReproduce,
          expectedBehavior: formData.expectedBehavior,
          actualBehavior: formData.actualBehavior
        }),
        ...(formData.feedbackType === 'feature' && {
          useCase: formData.useCase
        })
      };

      const response = await apiClient.post('/feedback/submit', submitData);

      if (screenshots.length > 0 && response.data.data.uniqueId) {
        const formDataUpload = new FormData();
        screenshots.forEach(file => {
          formDataUpload.append('screenshots', file);
        });

        await apiClient.post(`/feedback/${response.data.data.uniqueId}/attachments`, formDataUpload, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      }

      localStorage.removeItem('feedbackDraft');
      localStorage.removeItem('feedbackScreenshots');
      sessionStorage.setItem('feedbackJustSubmitted', 'true');

      setFormData({
        feedbackType: 'feedback',
        subject: '',
        description: '',
        reproducibility: 'always',
        stepsToReproduce: '',
        expectedBehavior: '',
        actualBehavior: '',
        useCase: '',
        priority: 'medium',
        browserInfo: navigator.userAgent,
        systemInfo: formData.systemInfo,
        urlContext: window.location.href,
        isPublic: false,
        createGithubIssue: true,
        screenshots: []
      });
      setScreenshots([]);
      setScreenshotPreviews([]);

      setShowSuccess(true);

      if (onSubmitSuccess) {
        onSubmitSuccess();
      }

      setTimeout(() => {
        setShowSuccess(false);
        onClose();
      }, 2000);
    } catch (error: any) {
      console.error('Failed to submit feedback:', error);

      if (error.response?.status === 413) {
        alert('Die Dateien sind zu groß. Bitte reduzieren Sie die Anzahl oder Größe der Screenshots.\n\nMaximale Gesamtgröße: 10 MB');
      } else if (error.response?.data?.errors) {
        const errorMessages = error.response.data.errors.map((err: any) =>
          `${err.path || err.param}: ${err.msg}`
        ).join('\n');
        alert(`Fehler bei der Validierung:\n${errorMessages}`);
      } else if (error.response?.data?.message) {
        alert(`Fehler: ${error.response.data.message}`);
      } else {
        alert('Fehler beim Senden des Feedbacks. Bitte versuchen Sie es erneut.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Backdrop - beim Screenshot-Capture verstecken */}
      <div
        className={clsx(
          'fixed inset-0 z-[10002] bg-black/60 backdrop-blur-sm transition-opacity duration-300',
          isOpen && !isCapturingScreenshot ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        onClick={onClose}
      />

      {/* Panel - Slide from right, beim Screenshot-Capture verstecken */}
      <div
        id="feedback-panel"
        className={clsx(
          'fixed right-0 top-0 z-[10003] h-full w-full max-w-md bg-white dark:bg-gray-900 shadow-2xl',
          'transform transition-transform duration-300 ease-out',
          isOpen && !isCapturingScreenshot ? 'translate-x-0' : 'translate-x-full'
        )}
        onWheel={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Feedback an RENDITEFUCHS
            </h2>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 dark:border-gray-800">
            <button
              onClick={() => handleTabChange('feedback')}
              className={clsx(
                'flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors',
                activeTab === 'feedback'
                  ? 'text-brand border-b-2 border-brand bg-brand/5'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800'
              )}
            >
              <MessageSquare className="w-4 h-4" />
              Feedback
            </button>
            <button
              onClick={() => handleTabChange('problem')}
              className={clsx(
                'flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors',
                activeTab === 'problem'
                  ? 'text-brand border-b-2 border-brand bg-brand/5'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800'
              )}
            >
              <AlertCircle className="w-4 h-4" />
              Problem
            </button>
            <button
              onClick={() => handleTabChange('feature')}
              className={clsx(
                'flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors',
                activeTab === 'feature'
                  ? 'text-brand border-b-2 border-brand bg-brand/5'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800'
              )}
            >
              <Sparkles className="w-4 h-4" />
              Feature
            </button>
          </div>

          {/* Form Content - Scrollable */}
          <div
            ref={scrollableRef}
            className="flex-1 overflow-y-auto px-6 py-5 scrollbar-styled"
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            <div className="space-y-5">
              {/* Subject */}
              <TWInput
                label="Betreff"
                required
                value={formData.subject}
                onChange={(v) => handleInputChange('subject', v)}
                maxLength={200}
                placeholder="Kurze Zusammenfassung..."
              />

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Beschreibung <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  maxLength={2000}
                  rows={6}
                  className="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:border-brand focus:ring-1 focus:ring-brand outline-none transition-colors resize-none"
                  placeholder={
                    activeTab === 'feedback'
                      ? 'Teilen Sie uns Ihre Gedanken mit...'
                      : activeTab === 'problem'
                      ? 'Beschreiben Sie das Problem...'
                      : 'Beschreiben Sie die gewünschte Funktion...'
                  }
                />
                <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 text-right">
                  {characterCount} / 2000 Zeichen
                </div>
              </div>

              {/* Problem-specific fields */}
              {activeTab === 'problem' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                      Reproduzierbarkeit
                    </label>
                    <TWSelect
                      value={formData.reproducibility ?? 'always'}
                      onChange={(v) => handleInputChange('reproducibility', v)}
                      options={[
                        { value: 'always', label: 'Immer' },
                        { value: 'sometimes', label: 'Manchmal' },
                        { value: 'rarely', label: 'Selten' },
                        { value: 'once', label: 'Einmalig' },
                      ]}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                      Schritte zur Reproduktion
                    </label>
                    <textarea
                      value={formData.stepsToReproduce}
                      onChange={(e) => handleInputChange('stepsToReproduce', e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:border-brand focus:ring-1 focus:ring-brand outline-none transition-colors resize-none"
                      placeholder="1. Schritt eins&#10;2. Schritt zwei&#10;3. ..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                      Schweregrad
                    </label>
                    <TWSelect
                      value={formData.priority ?? 'medium'}
                      onChange={(v) => handleInputChange('priority', v)}
                      options={[
                        { value: 'low', label: 'Niedrig - Kleine Unannehmlichkeit' },
                        { value: 'medium', label: 'Mittel - Beeintr\u00e4chtigt die Nutzung' },
                        { value: 'high', label: 'Hoch - Verhindert wichtige Funktionen' },
                        { value: 'critical', label: 'Kritisch - System nicht nutzbar' },
                      ]}
                    />
                  </div>
                </>
              )}

              {/* Feature-specific fields */}
              {activeTab === 'feature' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Use Case
                  </label>
                  <textarea
                    value={formData.useCase}
                    onChange={(e) => handleInputChange('useCase', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:border-brand focus:ring-1 focus:ring-brand outline-none transition-colors resize-none"
                    placeholder="Wie würde diese Funktion Ihnen helfen?"
                  />
                </div>
              )}

              {/* Screenshots */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Screenshots (max. 4)
                </label>
                <div className="flex flex-wrap gap-2">
                  {screenshotPreviews.map((preview, index) => (
                    <div
                      key={index}
                      className="relative w-20 h-20 rounded-lg overflow-hidden cursor-pointer group"
                      onClick={() => setSelectedPreview(preview)}
                    >
                      <img
                        src={preview}
                        alt={`Screenshot ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeScreenshot(index);
                        }}
                        className="absolute top-1 right-1 p-1 bg-red-500 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Screenshot löschen"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}

                  {screenshots.length < 4 && (
                    <>
                      <button
                        onClick={captureScreenshot}
                        className="w-20 h-20 flex flex-col items-center justify-center gap-1 border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg text-gray-500 dark:text-gray-400 hover:border-brand hover:text-brand transition-colors"
                      >
                        <Camera className="w-5 h-5" />
                        <span className="text-xs">Screenshot</span>
                      </button>

                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="w-20 h-20 flex flex-col items-center justify-center gap-1 border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg text-gray-500 dark:text-gray-400 hover:border-brand hover:text-brand transition-colors"
                      >
                        <Upload className="w-5 h-5" />
                        <span className="text-xs">Upload</span>
                      </button>

                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleFileUpload}
                        className="sr-only"
                      />
                    </>
                  )}
                </div>
              </div>

              {/* Public visibility */}
              <div className="space-y-3">
                <TWCheckbox
                  checked={formData.isPublic}
                  onChange={(e) => handleInputChange('isPublic', (e.target as HTMLInputElement).checked)}
                  label="Im Community-Dashboard veröffentlichen"
                />

                {/* Privacy warning */}
                {formData.isPublic && (
                  <div className="p-3 bg-status-warning/10 border border-amber-500/30 rounded-lg">
                    <div className="flex gap-3">
                      <AlertCircle className="w-5 h-5 text-status-warning shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-amber-700 dark:text-amber-400 mb-1">
                          Hinweis zur Veröffentlichung:
                        </p>
                        <ul className="text-xs text-amber-600 dark:text-amber-400/80 space-y-1 list-disc list-inside">
                          <li>Ihr Feedback wird für alle Community-Mitglieder sichtbar sein</li>
                          <li>Achten Sie darauf, keine persönlichen oder vertraulichen Daten zu teilen</li>
                          <li>Screenshots werden ebenfalls öffentlich angezeigt</li>
                          <li>Ihr Benutzername wird mit dem Feedback verknüpft</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {/* GitHub Issue wird immer automatisch erstellt */}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-800">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              Abbrechen
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !formData.subject || !formData.description}
              className={clsx(
                'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                isSubmitting || !formData.subject || !formData.description
                  ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                  : 'bg-brand text-gray-900 hover:bg-brand-hover'
              )}
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-gray-500 border-t-transparent rounded-full animate-spin" />
                  Sende...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Absenden
                </>
              )}
            </button>
          </div>
        </div>

        {/* Success Animation */}
        {showSuccess && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/95 dark:bg-gray-900/95 z-10">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500 flex items-center justify-center animate-[scale-in_0.3s_ease-out]">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-lg font-medium text-gray-900 dark:text-white">
                Vielen Dank für Ihr Feedback!
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Screenshot Preview Modal */}
      {selectedPreview && !isEditing && (
        <div
          className="fixed inset-0 z-[10004] flex items-center justify-center p-4 sm:p-6 lg:p-8"
          onClick={() => setSelectedPreview(null)}
        >
          <div className="absolute inset-0 bg-black/90" />
          <div
            className="relative flex flex-col max-w-[95vw] max-h-[95vh] bg-white dark:bg-gray-900 rounded-xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-800 shrink-0">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">Screenshot Vorschau</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsEditing(true)}
                  className="p-2 text-brand hover:bg-brand/10 rounded-lg transition-colors"
                  title="Screenshot bearbeiten"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    const index = screenshotPreviews.indexOf(selectedPreview);
                    if (index !== -1) {
                      removeScreenshot(index);
                      setSelectedPreview(null);
                    }
                  }}
                  className="p-2 text-red-500 hover:bg-status-error/10 rounded-lg transition-colors"
                  title="Screenshot löschen"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setSelectedPreview(null)}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                  title="Schließen"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            {/* Image Container */}
            <div className="flex-1 overflow-auto p-4 flex items-center justify-center bg-gray-100 dark:bg-gray-950">
              <img
                src={selectedPreview}
                alt="Screenshot Preview"
                className="max-w-full max-h-[calc(95vh-80px)] w-auto h-auto object-contain rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      )}

      {/* Screenshot Editor */}
      {selectedPreview && isEditing && (
        <ScreenshotEditor
          imageDataUrl={selectedPreview}
          onSave={handleSaveEditedScreenshot}
          onCancel={() => setIsEditing(false)}
        />
      )}
    </>
  );
};
